import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, ButtonGroup, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import {
  BUTTON_MENU_BUTTON_MOBILE_SCROLL_STEP,
  BUTTON_MENU_BUTTON_SCROLL_STEP,
  BUTTON_MENU_GROUP_SX,
  BUTTON_MENU_ROOT_SX,
  BUTTON_MENU_SCROLL_BUTTON_SX,
  BUTTON_MENU_SCROLL_CONTAINER_SX,
  BUTTON_MENU_SCROLL_EDGE_TOLERANCE,
  BUTTON_MENU_SCROLL_ERROR_BUTTON_SX,
  INITIAL_SCROLL_STATE,
} from './constants';
import FButtonMenuItem from './components/FButtonMenuItem';
import { getHiddenErrorState, mergeSxProps, scrollActiveButtonIntoView } from './utils';

import type { FButtonMenuProps, ScrollState } from './types';

const FButtonMenu = forwardRef<HTMLDivElement, FButtonMenuProps>(function FButtonMenu(props, ref) {
  const {
    options,
    navigateOptions,
    activeVariant = 'contained',
    ariaLabel = 'Section navigation',
    errorLabel = 'Has error',
    successLabel = 'Has success',
    fullWidth = true,
    disabled = false,
    variant = 'outlined',
    size = 'small',
    sx,
    scrollContainerSx,
    buttonGroupSx,
    scrollButtonProps,
    ...restButtonGroupProps
  } = props;
  const location = useLocation();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState<ScrollState>(INITIAL_SCROLL_STATE);
  const { sx: scrollButtonSx, ...restScrollButtonProps } = scrollButtonProps ?? {};
  const shouldRenderScrollControls = scrollState.canScrollLeft || scrollState.canScrollRight;

  const updateScrollState = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer == null) {
      setScrollState(INITIAL_SCROLL_STATE);
      return;
    }

    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    const hiddenErrorState = getHiddenErrorState(scrollContainer);

    setScrollState({
      canScrollLeft: scrollContainer.scrollLeft > BUTTON_MENU_SCROLL_EDGE_TOLERANCE,
      canScrollRight:
        scrollContainer.scrollLeft < maxScrollLeft - BUTTON_MENU_SCROLL_EDGE_TOLERANCE,
      ...hiddenErrorState,
    });
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer == null) {
      return undefined;
    }

    updateScrollState();
    scrollContainer.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    const resizeObserver = globalThis.ResizeObserver
      ? new globalThis.ResizeObserver(updateScrollState)
      : null;
    resizeObserver?.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [updateScrollState, options]);

  useEffect(() => {
    if (scrollContainerRef.current == null) {
      return;
    }

    scrollActiveButtonIntoView(scrollContainerRef.current);
    updateScrollState();
  }, [location.hash, location.pathname, location.search, options, updateScrollState]);

  const scrollByDirection = useCallback((direction: -1 | 1) => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer == null) {
      return;
    }

    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    const targetScrollLeft =
      scrollContainer.scrollLeft +
      (isMobileViewport ? BUTTON_MENU_BUTTON_MOBILE_SCROLL_STEP : BUTTON_MENU_BUTTON_SCROLL_STEP) *
        direction;

    scrollContainer.scrollTo({
      left:
        direction < 0 ? Math.max(0, targetScrollLeft) : Math.min(maxScrollLeft, targetScrollLeft),
      behavior: 'smooth',
    });
  }, [isMobileViewport]);

  const handleScrollLeft = useCallback(() => {
    scrollByDirection(-1);
  }, [scrollByDirection]);

  const handleScrollRight = useCallback(() => {
    scrollByDirection(1);
  }, [scrollByDirection]);

  const rootSx = useMemo(
    () =>
      mergeSxProps(fullWidth ? BUTTON_MENU_ROOT_SX : { ...BUTTON_MENU_ROOT_SX, width: 'auto' }, sx),
    [fullWidth, sx],
  );
  const mergedScrollContainerSx = useMemo(
    () =>
      mergeSxProps(
        mergeSxProps(
          BUTTON_MENU_SCROLL_CONTAINER_SX,
          shouldRenderScrollControls
            ? {
                justifyContent: 'flex-start',
              }
            : undefined,
        ),
        scrollContainerSx,
      ),
    [scrollContainerSx, shouldRenderScrollControls],
  );
  const mergedButtonGroupSx = useMemo(
    () =>
      mergeSxProps(
        mergeSxProps(BUTTON_MENU_GROUP_SX, shouldRenderScrollControls ? { mx: 0 } : undefined),
        buttonGroupSx,
      ),
    [buttonGroupSx, shouldRenderScrollControls],
  );
  const leftScrollButtonSx = useMemo(
    () =>
      mergeSxProps(
        mergeSxProps(
          BUTTON_MENU_SCROLL_BUTTON_SX,
          scrollState.hasHiddenLeftError ? BUTTON_MENU_SCROLL_ERROR_BUTTON_SX : undefined,
        ),
        scrollButtonSx,
      ),
    [scrollButtonSx, scrollState.hasHiddenLeftError],
  );
  const rightScrollButtonSx = useMemo(
    () =>
      mergeSxProps(
        mergeSxProps(
          BUTTON_MENU_SCROLL_BUTTON_SX,
          scrollState.hasHiddenRightError ? BUTTON_MENU_SCROLL_ERROR_BUTTON_SX : undefined,
        ),
        scrollButtonSx,
      ),
    [scrollButtonSx, scrollState.hasHiddenRightError],
  );

  return (
    <Box ref={ref} aria-label={ariaLabel} role="navigation" sx={rootSx}>
      {shouldRenderScrollControls ? (
        <IconButton
          {...restScrollButtonProps}
          aria-label={
            scrollState.hasHiddenLeftError ? `Scroll menu left (${errorLabel})` : 'Scroll menu left'
          }
          disabled={!scrollState.canScrollLeft}
          onClick={handleScrollLeft}
          size="small"
          sx={leftScrollButtonSx}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
      ) : null}
      <Box ref={scrollContainerRef} sx={mergedScrollContainerSx}>
        <ButtonGroup
          {...restButtonGroupProps}
          aria-label={ariaLabel}
          disabled={disabled}
          size={size}
          sx={mergedButtonGroupSx}
          variant={variant}
        >
          {options.map((option) => (
            <FButtonMenuItem
              key={option.value}
              activeVariant={activeVariant}
              buttonVariant={variant}
              disabled={disabled}
              errorLabel={errorLabel}
              navigateOptions={navigateOptions}
              option={option}
              successLabel={successLabel}
            />
          ))}
        </ButtonGroup>
      </Box>
      {shouldRenderScrollControls ? (
        <IconButton
          {...restScrollButtonProps}
          aria-label={
            scrollState.hasHiddenRightError
              ? `Scroll menu right (${errorLabel})`
              : 'Scroll menu right'
          }
          disabled={!scrollState.canScrollRight}
          onClick={handleScrollRight}
          size="small"
          sx={rightScrollButtonSx}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      ) : null}
    </Box>
  );
});

export default FButtonMenu;
